import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Select, type SelectOption } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import {
  ACTIVITY_TYPE_LABELS,
  VIBRATION_MODE_LABELS,
  type ActivityFilters as ActivityFiltersType,
  type ActivityType,
  type VibrationMode,
} from '@/types/activity';

interface ActivityFiltersProps {
  filters: ActivityFiltersType;
  onChange: (filters: Partial<ActivityFiltersType>) => void;
  onClear: () => void;
}

const activityTypeOptions: SelectOption[] = [
  { value: '', label: 'All Types' },
  ...Object.entries(ACTIVITY_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

const vibrationModeOptions: SelectOption[] = [
  { value: '', label: 'All Modes' },
  ...Object.entries(VIBRATION_MODE_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

export function ActivityFilters({
  filters,
  onChange,
  onClear,
}: ActivityFiltersProps) {
  const hasFilters =
    (filters.types?.length ?? 0) > 0 ||
    (filters.vibrationModes?.length ?? 0) > 0 ||
    filters.startDate ||
    filters.endDate;

  return (
    <div className="space-y-4 rounded-lg border border-border bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Filter className="h-4 w-4" />
          Filters
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            <X className="mr-1 h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          label="Activity Type"
          options={activityTypeOptions}
          value={filters.types?.[0] || ''}
          onChange={(e) =>
            onChange({
              types: e.target.value ? [e.target.value as ActivityType] : undefined,
            })
          }
        />

        <Select
          label="Vibration Mode"
          options={vibrationModeOptions}
          value={filters.vibrationModes?.[0] || ''}
          onChange={(e) =>
            onChange({
              vibrationModes: e.target.value
                ? [e.target.value as VibrationMode]
                : undefined,
            })
          }
        />

        <div className="flex gap-2">
          <Input
            label="Start Date"
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => onChange({ startDate: e.target.value || undefined })}
          />
          <Input
            label="End Date"
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => onChange({ endDate: e.target.value || undefined })}
          />
        </div>
      </div>
    </div>
  );
}
